/* SexyModest PDP behaviour — design_handoff_pdp (2026-08-19).
 *
 * Loaded only on templates/product.sm-pdp.json.
 *
 * Deliberately thin. Impulse's own theme.Product / theme.Variants already
 * own variant selection, price, media, sold-out state, history and the AJAX
 * cart; the new section reproduces their DOM contract so all of that keeps
 * working untouched. This file only adds what the handoff asks for on top:
 *
 *   1. glass carousel arrows  -> proxy Impulse's existing thumbnail arrows
 *   2. progress dashes        -> cap Flickity's page dots at 8
 *   3. hero badge             -> re-render on variant change, off one listener
 *   4. sticky add-to-cart bar -> reflect the buy box, never duplicate it
 *   5. size chart link        -> proxy the size chart app, hide if absent
 *
 * Nothing here holds selection state. Every interaction routes back to the
 * real radio inputs in the buy box and lets Impulse broadcast the result.
 */
(function () {
  'use strict';

  var MAX_DASHES = 8;

  function init(section) {
    if (section.smPdpReady) return;
    section.smPdpReady = true;

    initArrows(section);
    initGallerySelectsColor(section);
    initDashes(section);
    initBadges(section);
    initOptionLabels(section);
    initSoldOutLock(section);
    initSizeIndicator(section);
    initSwatchRing(section);
    initOptionValueFade(section);
    initSticky(section);
  }

  /* -------------------------------------------------------------- arrows */
  /* Impulse binds real click listeners to .product__thumb-arrow inside
     [data-product-thumbs] and routes them to slideshow.previous()/next().
     Proxying those is why this file never touches Flickity directly — the
     thumb strip is hidden on mobile but still in the DOM, and hidden
     elements still fire their listeners. */
  function initArrows(section) {
    var thumbs = section.querySelector('[data-product-thumbs]');
    if (!thumbs) return;

    section.querySelectorAll('[data-sm-arrow]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var dir = btn.getAttribute('data-sm-arrow');
        var target = thumbs.querySelector('.product__thumb-arrow--' + (dir === 'prev' ? 'prev' : 'next'));
        if (target) target.click();
      });
    });
  }

  /* ------------------------------------------------- gallery selects colour */
  /* Swiping the gallery selects that photo's colour.

     Without this the gallery and the buy box drift apart: you could swipe to
     a red photo while the price, availability, badge and Add to Cart were all
     still the navy variant — the badge looking "stuck" was this, not the
     badge.

     Selection is made by clicking the real colour radio, so Impulse drives
     everything downstream exactly as it does for a swatch tap. Nothing here
     duplicates its state.

     No feedback loop: selecting a colour makes Impulse jump the gallery to
     that colour's photo, which lands where we already are, and the guard
     below skips an input that's already checked — so a click is never
     re-issued. */
  function initGallerySelectsColor(section) {
    var slider = section.querySelector('[data-product-photos]');
    var colorGroup = section.querySelector('.sm-pdp-option--color .variant-input-wrap');
    if (!slider || !colorGroup) return;
    if (!section.querySelector('[data-sm-media-color]')) return;

    var pending = false;

    function sync() {
      pending = false;

      var slide = slider.querySelector('.product-main-slide.is-selected');
      if (!slide || !slide.dataset.mediaId) return;

      var node = section.querySelector('[data-sm-media-color="' + slide.dataset.mediaId + '"]');
      if (!node) return; // photo isn't assigned to a variant — leave the selection alone
      var color = node.textContent.trim();

      // Compared by value rather than by selector so colour names with
      // punctuation ("Brick-Dark", "Kate-Blue") need no escaping.
      var inputs = colorGroup.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].value !== color) continue;
        if (!inputs[i].checked && !inputs[i].disabled) inputs[i].click();
        return;
      }
    }

    /* Debounced, not coalesced per frame. Flickity rewrites slide classes
       continuously while a drag is in progress, so a per-frame schedule
       ran sync() dozens of times mid-swipe - and each run can fire a
       radio click, which triggers Impulse's whole variant update plus the
       Essential Set refresh on top. That is what made the gallery feel
       like it was catching.

       Waiting for the classes to stop changing means the colour switches
       once, after the carousel settles, and the swipe itself stays free
       of work. */
    var timer = null;
    function schedule() {
      pending = true;
      if (timer) clearTimeout(timer);
      timer = setTimeout(sync, 140);
    }

    // Flickity marks the current slide with a class, so watch for that rather
    // than reaching into it. Coalesced to one pass per frame because it
    // rewrites classes on several slides per transition.
    new MutationObserver(schedule).observe(slider, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /* -------------------------------------------------------------- dashes */
  /* Flickity renders one dot per media. CSS turns them into thin segments.
     Past MAX_DASHES that would shred into hairlines, so the extras are
     hidden and the active dash is mapped proportionally instead — the same
     mapping the design reference uses. */
  function initDashes(section) {
    var gallery = section.querySelector('[data-sm-gallery]');
    if (!gallery) return;

    watchFor(gallery, '.flickity-page-dots', function (dots) {
      var all = Array.prototype.slice.call(dots.children);
      if (all.length <= MAX_DASHES) return;

      dots.classList.add('sm-dots-capped');
      all.forEach(function (dot, i) {
        if (i >= MAX_DASHES) dot.classList.add('sm-dot-hidden');
      });

      var sync = function () {
        var current = all.indexOf(dots.querySelector('.dot.is-selected'));
        if (current < 0) return;
        var active = Math.round((current / (all.length - 1)) * (MAX_DASHES - 1));
        all.forEach(function (dot, i) {
          dot.classList.toggle('sm-is-active', i === active);
        });
      };

      sync();
      new MutationObserver(sync).observe(dots, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    });
  }

  /* -------------------------------------------------------------- badges */
  /* Variant-scoped, so it re-evaluates on every variant change.

     Reads the badge off a per-variant node rather than a parsed JSON map.
     The map version sat behind a JSON.parse in a try/catch that returned on
     failure — so if the JSON was ever malformed the listener was never
     attached at all, and the server-rendered badge stayed frozen on screen
     for every variant with nothing to indicate why.

     Listens on both events Impulse already dispatches for a variant change:
     `variantChange` on the section and `variant:change` on the document.
     That is not a second watcher — it's the same dispatch, caught in two
     places — and the document listener keeps working if the section element
     is swapped out from under us, which the theme editor does on every
     settings change. */
  function initBadges(section) {
    var stack = section.querySelector('[data-sm-badges]');
    if (!stack) return;

    function apply(evt) {
      var variant = evt.detail && evt.detail.variant;
      if (!variant) {
        stack.setAttribute('data-sm-badge', '');
        return;
      }
      // Scoped to this section, so an event for another product on the page
      // finds nothing and is ignored.
      var node = section.querySelector('[data-sm-badge-for="' + variant.id + '"]');
      if (!node) return;
      stack.setAttribute('data-sm-badge', node.textContent.trim());
    }

    section.addEventListener('variantChange', apply);
    document.addEventListener('variant:change', apply);
  }

  /* ------------------------------------------------------- option labels */
  /* Shows the selected value beside each option label. Impulse only ships
     this for colour (updateColorName, driven by its swatch inputs), so size
     and any other option need it here.

     Reads the checked input rather than tracking state: Impulse updates the
     radios before it dispatches variantChange, so by the time this runs the
     DOM is already the source of truth. */
  function initOptionLabels(section) {
    var labels = section.querySelectorAll('[data-sm-option-value]');
    if (!labels.length) return;

    function sync() {
      labels.forEach(function (label) {
        var option = label.closest('.sm-pdp-option');
        if (!option) return;
        var checked = option.querySelector('input[type="radio"]:checked');
        if (checked) label.textContent = checked.value;
      });
    }

    section.addEventListener('variantChange', sync);
    sync();
  }

  /* --------------------------------------------------------- sold out lock */
  /* Impulse crosses out unpurchasable options but deliberately leaves them
     selectable. We don't want that — a shopper shouldn't be able to land on
     a combination they can't buy.

     Mirrors Impulse's own .disabled class onto the input's disabled
     property, so it can't be clicked, tabbed to, or driven by the sticky
     bar's popover (which selects by clicking the real input, and a disabled
     input ignores that — the same rule applies in both places for free).

     The current selection is never disabled. Impulse can leave the checked
     option marked unavailable after a colour switch, and disabling it would
     mean the buy box is showing a selection the shopper can't see marked;
     leaving it enabled lets them move off it. Reading it back is safe either
     way — Impulse's _getCurrentOptions goes by .checked, which a disabled
     input still reports.

     Runs a frame late so Impulse's own availability pass, which re-marks
     every group on each change, has finished first. */
  function initSoldOutLock(section) {
    var inputs = section.querySelectorAll('.sm-pdp-option input[type="radio"]');
    if (!inputs.length) return;

    function sync() {
      inputs.forEach(function (input) {
        input.disabled = input.classList.contains('disabled') && !input.checked;
      });
    }

    section.addEventListener('variantChange', function () {
      requestAnimationFrame(sync);
    });
    requestAnimationFrame(sync);
  }

  /* -------------------------------------------------------------- sticky */
  function initSticky(section) {
    var bar = section.querySelector('[data-sm-sticky]');
    if (!bar) return;

    var mainAtc = section.querySelector('[data-add-to-cart]');
    if (!mainAtc) return;

    var colorIndex = parseInt(bar.getAttribute('data-color-index'), 10);
    var sizeIndex = parseInt(bar.getAttribute('data-size-index'), 10);
    var groups = section.querySelectorAll('.sm-pdp__block--options .variant-input-wrap');

    var colorGroup = colorIndex > -1 ? groups[colorIndex] : null;
    var sizeGroup = sizeIndex > -1 ? groups[sizeIndex] : null;

    /* The bar animates in and out rather than switching on display.
       hidden stays as the thing that takes it out of the accessibility tree
       and out of the layout, but display:none cancels a transition, so it is
       applied only after the exit has finished. The class does the moving.

       The timer is cancelled if the bar is shown again mid-exit, or scrolling
       back and forth across the threshold would hide a bar on its way in. */
    var BAR_EXIT_MS = 260;

    function showBar() {
      if (bar.smHideTimer) {
        clearTimeout(bar.smHideTimer);
        bar.smHideTimer = null;
      }
      if (!bar.hidden && bar.classList.contains('is-in')) return;
      bar.hidden = false;
      /* Read a layout property between unhiding and adding the class, or the
         browser folds both into one style pass and there is no closed state
         to transition from. */
      void bar.offsetWidth;
      bar.classList.add('is-in');
    }

    function hideBar() {
      if (bar.hidden) return;
      bar.classList.remove('is-in');
      closeAll();
      if (bar.smHideTimer) clearTimeout(bar.smHideTimer);
      bar.smHideTimer = setTimeout(function () {
        bar.hidden = true;
        bar.smHideTimer = null;
      }, BAR_EXIT_MS);
    }

    var thumb = bar.querySelector('[data-sm-sticky-thumb]');
    var price = bar.querySelector('[data-sm-sticky-price]');
    var sizeChip = bar.querySelector('[data-sm-sticky-size]');
    var dot = bar.querySelector('[data-sm-sticky-dot]');
    var atc = bar.querySelector('[data-sm-sticky-atc]');
    var atcText = bar.querySelector('[data-sm-sticky-atc-text]');

    var dotFill = null;
    if (dot) {
      dotFill = document.createElement('span');
      dot.appendChild(dotFill);
    }

    /* Popovers are filled with clones of the buy box's own labels. Cloning
       the <label> and not the <input> is the point: a second radio sharing
       the same name would fight the real form. The clone is decoration; the
       click goes to the original input. */
    function buildPopover(kind, group) {
      var pop = bar.querySelector('[data-sm-sticky-pop="' + kind + '"]');
      if (!pop || !group) return null;

      group.querySelectorAll('.variant-input').forEach(function (wrap) {
        var source = wrap.querySelector('label');
        var input = wrap.querySelector('input');
        if (!source || !input) return;

        var clone = source.cloneNode(true);
        clone.removeAttribute('for');
        clone.removeAttribute('id');
        clone.setAttribute('role', 'button');
        clone.setAttribute('tabindex', '0');
        clone._smSource = source;
        clone._smInput = input;

        var choose = function () {
          input.click();
          closeAll();
        };
        clone.addEventListener('click', choose);
        clone.addEventListener('keydown', function (evt) {
          if (evt.key === 'Enter' || evt.key === ' ') {
            evt.preventDefault();
            choose();
          }
        });

        pop.appendChild(clone);
      });

      return pop;
    }

    var pops = {
      color: buildPopover('color', colorGroup),
      size: buildPopover('size', sizeGroup)
    };

    function closeAll() {
      Object.keys(pops).forEach(function (k) {
        if (pops[k]) pops[k].hidden = true;
      });
      bar.querySelectorAll('[data-sm-sticky-toggle]').forEach(function (b) {
        b.setAttribute('aria-expanded', 'false');
      });
    }

    bar.querySelectorAll('[data-sm-sticky-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function (evt) {
        evt.stopPropagation();
        var kind = btn.getAttribute('data-sm-sticky-toggle');
        var pop = pops[kind];
        if (!pop) return;
        var willOpen = pop.hidden;
        closeAll();
        pop.hidden = !willOpen;
        btn.setAttribute('aria-expanded', String(willOpen));
      });
    });

    document.addEventListener('click', function (evt) {
      if (!bar.contains(evt.target)) closeAll();
    });
    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape') closeAll();
    });

    if (atc) {
      atc.addEventListener('click', function () {
        mainAtc.click();
      });
    }

    /* Mirror, never store. Every value below is read back out of the buy box
       after Impulse has finished updating it. */
    function sync(variant) {
      Object.keys(pops).forEach(function (kind) {
        var pop = pops[kind];
        if (!pop) return;
        Array.prototype.forEach.call(pop.children, function (clone) {
          if (!clone._smSource) return;
          clone.className = clone._smSource.className;
          if (clone._smInput.checked) clone.classList.add('sm-is-selected');
        });
      });

      var checkedColor = colorGroup && colorGroup.querySelector('input:checked');
      if (dotFill && checkedColor) {
        var fill = checkedColor.parentElement.querySelector('.sm-swatch-ringed__fill');
        if (fill) dotFill.setAttribute('style', fill.getAttribute('style') || '');
      }

      var checkedSize = sizeGroup && sizeGroup.querySelector('input:checked');
      if (sizeChip && checkedSize) sizeChip.textContent = checkedSize.value;

      var mainPrice = section.querySelector('[data-product-price]');
      if (price && mainPrice) price.textContent = mainPrice.textContent.trim();

      if (atc) {
        atc.disabled = mainAtc.disabled;
        var mainText = section.querySelector('[data-add-to-cart-text]');
        if (atcText && mainText) atcText.textContent = mainText.textContent.trim();
      }

      if (thumb && variant && variant.featured_image && variant.featured_image.src) {
        thumb.src = variant.featured_image.src.replace(/(\.[a-z]+)(\?|$)/i, '_108x$1$2');
        thumb.removeAttribute('srcset');
      }
    }

    section.addEventListener('variantChange', function (evt) {
      // Impulse updates price/button text on the same event; let it land first.
      var variant = evt.detail && evt.detail.variant;
      requestAnimationFrame(function () {
        sync(variant);
      });
    });

    sync(null);

    /* Show the bar only once the primary Add to Cart has scrolled UP out of
       view.

       Not-intersecting on its own isn't enough: at the top of the page the
       button is out of view because it's still below the fold, which showed
       the bar immediately on load — before the shopper had even reached the
       real button. boundingClientRect.top separates the two cases: negative
       means the button has gone off the top, so it's genuinely behind us. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          if (scrolledPast) showBar(); else hideBar();
        },
        { threshold: 0 }
      ).observe(mainAtc);
    }
  }

  /* ------------------------------------------------------------- helpers */
  /* Flickity builds its dots after this script runs, so wait for the node
     rather than assuming it exists. Gives up after 8s. */
  function watchFor(root, selector, cb) {
    var existing = root.querySelector(selector);
    if (existing) {
      cb(existing);
      return;
    }
    var observer = new MutationObserver(function () {
      var el = root.querySelector(selector);
      if (!el) return;
      observer.disconnect();
      cb(el);
    });
    observer.observe(root, { childList: true, subtree: true });
    setTimeout(function () {
      observer.disconnect();
    }, 8000);
  }

  function boot() {
    document.querySelectorAll('.sm-pdp[data-section-type="product"]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Publish the sticky bar's height as --sm-chat-lift so the chat bubble
     can clear it. Measured rather than hardcoded: the bar wraps to two
     rows on narrow phones, so any fixed number is wrong on one of them.
     No-ops on pages with no sticky bar. */
  function trackStickyHeight() {
    var bar = document.querySelector('[data-sm-sticky]');
    if (!bar) return;
    var apply = function () {
      var h = bar.hidden ? 0 : bar.offsetHeight;
      document.documentElement.style.setProperty(
        '--sm-chat-lift', h ? (h + 12) + 'px' : '0px'
      );
    };
    new MutationObserver(apply).observe(bar, {
      attributes: true,
      attributeFilter: ['hidden']
    });
    if (window.ResizeObserver) new ResizeObserver(apply).observe(bar);
    window.addEventListener('resize', apply);
    apply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trackStickyHeight);
  } else {
    trackStickyHeight();
  }

  /* Fabric pill disclosure.

     Delegated from document rather than bound to the button, so it works
     whenever the badge renders - including a theme-editor re-render, which
     replaces the markup without reloading the page.

     Close on outside click and on Escape. No overlay is involved, so there
     is nothing that can keep swallowing clicks if a close is ever missed. */
  /* hidden is display:none, which cancels a transition outright - set it on
     the way out and the panel vanishes instead of leaving. So the class
     drives the animation and hidden is applied after the exit finishes,
     which keeps the element out of the accessibility tree while closed
     without costing the animation.

     Kept a hair longer than the 220ms exit so the transition has landed
     before the element goes; the timer is cancelled if the panel is
     reopened mid-close, or a quick double tap would hide a panel that is
     on its way back in. */
  var FABRIC_EXIT_MS = 260;

  function closeFabricPop(btn) {
    var pop = document.getElementById(btn.getAttribute('aria-controls'));
    btn.setAttribute('aria-expanded', 'false');
    if (!pop) return;
    pop.classList.remove('is-open');
    if (pop.smCloseTimer) clearTimeout(pop.smCloseTimer);
    pop.smCloseTimer = setTimeout(function () {
      pop.hidden = true;
      pop.smCloseTimer = null;
    }, FABRIC_EXIT_MS);
  }

  function openFabricPop(btn) {
    var pop = document.getElementById(btn.getAttribute('aria-controls'));
    btn.setAttribute('aria-expanded', 'true');
    if (!pop) return;
    if (pop.smCloseTimer) {
      clearTimeout(pop.smCloseTimer);
      pop.smCloseTimer = null;
    }
    pop.hidden = false;
    /* Read a layout property between unhiding and adding the class. Without
       it the browser coalesces both into one style pass, the element never
       exists in its closed state, and there is nothing to transition from -
       the panel would simply appear. */
    void pop.offsetWidth;
    pop.classList.add('is-open');
  }

  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest && evt.target.closest('[data-sm-fabric-toggle]');

    // Close any pill that is open and is not the one just clicked.
    var open = document.querySelectorAll('[data-sm-fabric-toggle][aria-expanded="true"]');
    for (var i = 0; i < open.length; i++) {
      if (open[i] !== btn) closeFabricPop(open[i]);
    }

    if (!btn) return;
    // A click inside the panel itself must not toggle it shut.
    if (evt.target.closest('[data-sm-fabric-pop]')) return;

    evt.preventDefault();
    if (btn.getAttribute('aria-expanded') === 'true') {
      closeFabricPop(btn);
    } else {
      openFabricPop(btn);
    }
  });

  document.addEventListener('keydown', function (evt) {
    if (evt.key !== 'Escape') return;
    var open = document.querySelectorAll('[data-sm-fabric-toggle][aria-expanded="true"]');
    for (var i = 0; i < open.length; i++) {
      closeFabricPop(open[i]);
      open[i].focus();
    }
  });

  /* ------------------------------------------------- swatch ring + labels */
  /* The colour ring travels between swatches, same language as the size
     block. Same architecture too, guards included: rect deltas against the
     wrap (offsetLeft lies here, the pills' wrappers are positioned), zero
     measurements ignored, label found by sibling walk, self-healing
     visibility, and reduced motion checked in script because the movement
     is keyframed where a media query cannot reach.

     Circles cannot stretch like the block without looking wrong, so the
     life comes from a small settle on arrival instead. */
  function initSwatchRing(section) {
    var wraps = section.querySelectorAll('.variant-input-wrap');

    Array.prototype.forEach.call(wraps, function (wrap) {
      var swatches = wrap.querySelectorAll('label.sm-pdp-swatch');
      if (swatches.length < 2) return;

      var ring = document.createElement('span');
      ring.className = 'sm-swatch-ring';
      wrap.appendChild(ring);
      wrap.classList.add('sm-has-ring');

      var at = null;

      function checkedSwatch() {
        var input = wrap.querySelector('input[type="radio"]:checked');
        if (!input) return null;
        var el = input.nextElementSibling;
        while (el && el.tagName !== 'LABEL') el = el.nextElementSibling;
        return el;
      }

      function place(animate) {
        var label = checkedSwatch();
        if (!label) return;
        if (!animate) ring.classList.remove('is-ready');

        var wrapRect = wrap.getBoundingClientRect();
        var rect = label.getBoundingClientRect();
        var x = rect.left - wrapRect.left;
        var y = rect.top - wrapRect.top;
        var w = rect.width;
        var h = rect.height;
        if (!w || !h) return;

        ring.style.width = w + 'px';
        ring.style.height = h + 'px';
        ring.style.transform = 'translate(' + x + 'px,' + y + 'px)';

        var still =
          window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        var moved = at && (at.x !== x || at.y !== y);
        if (animate && !still && moved && ring.animate) {
          /* One continuous move at any distance - the ring never vanishes.
             The capsule stretch carries the travel, growing with distance,
             and the duration grows with distance too: a fixed clock meant a
             cross-row jump moved six times faster than a neighbour hop, and
             that velocity is what read as rough when it crossed the other
             swatches. Slower over distance, the pass-through reads as a
             glide.

             Row changes travel round - a tilted capsule reads wrong - but
             get the same distance-scaled clock. */
          var sameRow = Math.abs(y - at.y) < 2;
          var travel = Math.hypot(x - at.x, y - at.y);
          /* The stretch ramps with distance instead of slamming into its
             cap: the old divisor maxed it out by the second swatch, so a
             short crossing got the same dramatic capsule as a full-row
             jump, and a fat ring hugging a swatch mid-flight read as a
             mis-selection flash. Now a 2-3 swatch crossing sits around
             25-30%, and only genuinely long jumps reach 35. Thinner while
             stretched, for the same reason - mid-flight it should read as
             motion, not as a ring resting on the wrong colour. */
          var st = sameRow ? 1 + Math.min(travel / (w * 6), 0.35) : 1;
          var sq = 1 - (st - 1) * 0.55;
          var mx = (at.x + x) / 2;
          var my = (at.y + y) / 2;
          var dur = Math.min(440 + travel * 0.7, 700);
          ring.animate(
            [
              { transform: 'translate(' + at.x + 'px,' + at.y + 'px) scale(1, 1)' },
              {
                transform: 'translate(' + mx + 'px,' + my + 'px) scale(' + st + ', ' + sq + ')',
                offset: 0.4
              },
              { transform: 'translate(' + x + 'px,' + y + 'px) scale(1, 1)', offset: 0.72 },
              { transform: 'translate(' + x + 'px,' + y + 'px) scale(1.04, 1.04)', offset: 0.86 },
              { transform: 'translate(' + x + 'px,' + y + 'px) scale(1, 1)' }
            ],
            { duration: dur, easing: 'cubic-bezier(.4, 0, .2, 1)' }
          );
        }
        at = { x: x, y: y };
        if (animate) ring.classList.add('is-ready');

        if (!animate) {
          requestAnimationFrame(function () {
            requestAnimationFrame(function () { ring.classList.add('is-ready'); });
          });
        }
      }

      wrap.addEventListener('change', function () { place(true); });
      /* Selecting a colour can move the size selection - Impulse reassigns
         the checked radio when the current size is unavailable in the new
         colour, and a programmatic .checked write fires no change event.
         The indicator sat on a disabled size after a colour swap because of
         exactly that. Impulse announces those moves as variantChange on the
         section; the rAF matches initSoldOutLock, which listens the same
         way for the same timing reason. */
      section.addEventListener('variantChange', function () {
        requestAnimationFrame(function () { place(true); });
      });
      if (window.ResizeObserver) {
        new ResizeObserver(function () { place(true); }).observe(wrap);
      }
      window.addEventListener('resize', function () { place(true); });

      place(false);
    });
  }

  /* The option's name - "Heather Gray", "L" - wipes when it changes: the
     new name reveals left to right while the old one erases left to right,
     one overtaking the other.

     Both texts have to exist at once for that, so the old text is kept as
     a ghost child laid over the span. The label sync has already written
     the new text by the time this runs (it registered its listener first),
     so the old value comes from a cache kept here, not from the DOM. The
     sync writes with textContent, which conveniently also destroys any
     mid-flight ghost from a previous change before this adds the next. */
  function initOptionValueFade(section) {
    var wraps = section.querySelectorAll('.variant-input-wrap');
    Array.prototype.forEach.call(wraps, function (wrap) {
      var option = wrap.closest('.sm-pdp-option');
      var value = option && option.querySelector('[data-sm-option-value]');
      if (!value || !value.animate) return;

      var prev = value.textContent;

      wrap.addEventListener('change', function () {
        var next = value.textContent;
        var old = prev;
        prev = next;
        if (next === old) return;
        if (
          window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) return;

        var ghost = document.createElement('span');
        ghost.className = 'sm-option-ghost';
        ghost.textContent = old;
        ghost.setAttribute('aria-hidden', 'true');
        value.appendChild(ghost);

        var timing = { duration: 420, easing: 'cubic-bezier(.4, 0, .2, 1)' };

        /* Two things make this read as a crossfade rather than a swap.

           The masks are NOT complements. With matching edges, wherever the
           old word was visible the new one was exactly hidden - no point on
           screen ever showed both, which is why it felt like disappear-
           then-appear. The feather is very wide now (20-80), so each letter
           position spends most of the duration partially showing both
           words.

           And the ghost fades globally to zero on top of its sweep, so the
           old word is genuinely fading off as a whole while the new one
           materialises through it. Ghost opacity is safe to animate - it is
           the child - whereas fading the span itself would drag the ghost
           with it. */
        var soft = 'linear-gradient(90deg, #000 20%, transparent 80%)';
        var softGhost = 'linear-gradient(90deg, transparent 20%, #000 80%)';
        var maskOK =
          window.CSS && CSS.supports &&
          (CSS.supports('mask-size', '220% 100%') ||
           CSS.supports('-webkit-mask-size', '220% 100%'));

        var wipe;
        if (maskOK) {
          [[value, soft], [ghost, softGhost]].forEach(function (pair) {
            var el = pair[0];
            el.style.webkitMaskImage = pair[1];
            el.style.maskImage = pair[1];
            el.style.webkitMaskSize = '220% 100%';
            el.style.maskSize = '220% 100%';
            el.style.webkitMaskRepeat = 'no-repeat';
            el.style.maskRepeat = 'no-repeat';
          });
          value.animate(
            [
              { maskPosition: '100% 0', webkitMaskPosition: '100% 0' },
              { maskPosition: '0% 0', webkitMaskPosition: '0% 0' }
            ],
            timing
          );
          wipe = ghost.animate(
            [
              { maskPosition: '100% 0', webkitMaskPosition: '100% 0', opacity: 1 },
              { maskPosition: '0% 0', webkitMaskPosition: '0% 0', opacity: 0 }
            ],
            timing
          );
        } else {
          /* No mask support: the hard-edged clip wipe, same travel. */
          value.animate(
            [{ clipPath: 'inset(0 100% 0 0)' }, { clipPath: 'inset(0 0 0 0)' }],
            timing
          );
          wipe = ghost.animate(
            [{ clipPath: 'inset(0 0 0 0)' }, { clipPath: 'inset(0 0 0 100%)' }],
            timing
          );
        }
        wipe.onfinish = function () {
          if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
          /* The span keeps no mask at rest - a lingering mask would clip
             the next value the sync writes. */
          value.style.webkitMaskImage = '';
          value.style.maskImage = '';
        };
      });
    });
  }

  /* ---------------------------------------------- size switcher indicator */
  /* One dark block that slides between sizes, instead of the fill switching
     off in one pill and on in another.

     Built in script rather than markup so the whole treatment is behind a
     capability check: no script, no indicator, no .sm-has-indicator, and the
     pills keep the appearance they have always had. The CSS restyles the
     control into a track, so it must not apply without something to slide.

     Colour swatches are excluded - they are round, individually coloured,
     and there is nothing for a single block to represent. */
  function initSizeIndicator(section) {
    var wraps = section.querySelectorAll('.variant-input-wrap');

    Array.prototype.forEach.call(wraps, function (wrap) {
      var labels = wrap.querySelectorAll('label.sm-pdp-size');
      if (labels.length < 2) return;
      if (wrap.querySelector('.sm-pdp-swatch')) return;

      var bar = document.createElement('span');
      bar.className = 'sm-size-indicator';
      wrap.appendChild(bar);
      wrap.classList.add('sm-has-indicator');

      /* The label is the input's next sibling in this markup. Walking the DOM
         beats a [for=] selector built from an id that carries url-encoded
         option values - one unescaped character there and the lookup returns
         null, which used to blank the indicator. */
      function checkedLabel() {
        var input = wrap.querySelector('input[type="radio"]:checked');
        if (!input) return null;
        var el = input.nextElementSibling;
        while (el && el.tagName !== 'LABEL') el = el.nextElementSibling;
        return el;
      }

      /* Reading geometry costs a layout, which is why this runs on selection
         rather than on every frame. */
      var atX = null;

      function place(animate) {
        var label = checkedLabel();
        if (!label) return;
        if (!animate) bar.classList.remove('is-ready');

        /* Measured with getBoundingClientRect rather than offsetLeft.
           offsetLeft reports against the nearest positioned ancestor, and
           .variant-input is positioned to stack the pills above the block -
           so every label reported roughly zero and the indicator never
           moved. Rect deltas are independent of who the offsetParent is. */
        var wrapRect = wrap.getBoundingClientRect();
        var rect = label.getBoundingClientRect();
        var x = rect.left - wrapRect.left;
        var y = rect.top - wrapRect.top;
        var w = rect.width;
        var h = rect.height;

        /* Ignore a zero measurement. Impulse rewrites this control while it
           swaps the variant, and anything measured mid-rewrite comes back
           0x0 - which set the indicator to no width at all, read as the fill
           vanishing, and then snapped back when the next measurement landed.
           Keeping the last good geometry is always better than drawing
           nothing. */
        if (!w || !h) return;

        bar.style.width = w + 'px';
        bar.style.height = h + 'px';

        /* The stretch. A block that only translates reads mechanical; one
           that elongates into the move and settles reads like it has mass.
           Squash and stretch, straight out of the Disney principles - the
           one place this design language allows a little give, because it is
           the object deforming rather than the layout bouncing.

           Scaled by distance and capped at 18%, so a neighbouring size
           barely stretches and a jump across the row does. Symmetrical
           around the centre, which keeps both edges moving. */
        /* prefers-reduced-motion is checked here rather than in CSS: this
           animation is keyframed in script, so a media query in the
           stylesheet cannot reach it. */
        var still =
          window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        var canAnimate = animate && !still && atX !== null && atX !== x && bar.animate;

        /* Resting position is set first and instantly - there is no CSS
           transition on transform any more. The keyframes then play over the
           top, from where the block was to where it now is, so the element
           is never between two owners. */
        bar.style.transform = 'translate(' + x + 'px,' + y + 'px)';

        if (canAnimate) {
          var travel = Math.abs(x - atX);
          var stretch = 1 + Math.min(travel / (w * 4), 0.18);
          bar.animate(
            [
              { transform: 'translate(' + atX + 'px,' + y + 'px) scaleX(1)' },
              {
                transform:
                  'translate(' + ((atX + x) / 2) + 'px,' + y + 'px) scaleX(' + stretch + ')',
                offset: 0.5
              },
              { transform: 'translate(' + x + 'px,' + y + 'px) scaleX(1)' }
            ],
            { duration: 300, easing: 'cubic-bezier(.4, 0, .2, 1)' }
          );
        }
        atX = x;
        /* Placed successfully, so it is visible - whatever happened before.
           Self-healing beats a one-way flag that can strand the indicator
           hidden after a single bad measurement.

           Only on an animated call: the first placement deliberately holds
           is-ready back until the transform has painted, or the block
           travels in from the corner on load. */
        if (animate) bar.classList.add('is-ready');

        if (!animate) {
          /* Let the placement paint before transitions are allowed, or the
             block animates in from the corner on first load. */
          requestAnimationFrame(function () {
            requestAnimationFrame(function () { bar.classList.add('is-ready'); });
          });
        }
      }

      wrap.addEventListener('change', function () { place(true); });
      /* Same reason as the swatch ring: Impulse moves the checked size
         programmatically when a colour swap makes it unavailable, and that
         write fires no change event. variantChange is how it announces it. */
      section.addEventListener('variantChange', function () {
        requestAnimationFrame(function () { place(true); });
      });

      /* Fonts and images landing late change the pills' measured size, and a
         stale indicator is worse than none. ResizeObserver catches both, plus
         orientation changes, without polling. */
      if (window.ResizeObserver) {
        new ResizeObserver(function () { place(true); }).observe(wrap);
      }
      window.addEventListener('resize', function () { place(true); });

      place(false);
    });
  }

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', function (evt) {
    var el = evt.target.querySelector('.sm-pdp[data-section-type="product"]');
    if (el) init(el);
  });
})();
